import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaArrowRight, FaCheckCircle, FaClock, FaShieldAlt } from 'react-icons/fa';

export default function CTASection() {
  return (
    <section className="relative overflow-hidden scroll-mt-20">
      {/* Artistic Gradient Background - Matching Hero */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0d2847] to-[#1a3a5c]" />
      
      {/* Artistic overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/80 via-[#0d2847]/40 to-transparent" />
      
      {/* Radial glow in the center */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(232,150,13,0.06)_0%,_transparent_70%)]" />
      
      {/* Gold accent glow from top right */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[radial-gradient(ellipse,_rgba(232,150,13,0.05)_0%,_transparent_70%)] -translate-y-1/2 translate-x-1/4" />
      
      {/* Blue accent glow from bottom left */}
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[radial-gradient(ellipse,_rgba(7,69,120,0.08)_0%,_transparent_70%)] translate-y-1/2 -translate-x-1/4" />

      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-[0.02]">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <pattern id="cta-grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="white" strokeWidth="0.5"/>
          </pattern>
          <rect width="100" height="100" fill="url(#cta-grid)" />
        </svg>
      </div>
      
      {/* Decorative circles - more subtle */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
      <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2" />

      <div className="container-custom relative z-10 py-16 md:py-20">
        <div className="grid lg:grid-cols-2 gap-10 md:gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center lg:text-left"
          >
            <span className="inline-block bg-micropoint-gold/20 text-micropoint-gold px-4 py-1 rounded-full text-sm font-semibold mb-4 border border-micropoint-gold/20">
              🚀 Let's Get Started
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-display leading-tight">
              Ready to Transform <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-micropoint-gold via-yellow-400 to-amber-300">
                Your Business?
              </span>
            </h2>
            <p className="text-white/70 text-base md:text-lg mt-4 max-w-lg mx-auto lg:mx-0">
              Join 150+ organizations that trust Micropoint Systems for their digital transformation journey.
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 mt-6">
              {[
                { icon: FaCheckCircle, label: '10+ Years Experience' },
                { icon: FaClock, label: '24/7 Support' },
                { icon: FaShieldAlt, label: 'Enterprise Security' },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <span key={index} className="flex items-center gap-2 text-white/60 text-sm">
                    <Icon className="text-micropoint-gold text-xs" /> {item.label}
                  </span>
                );
              })}
            </div>

            {/* Contact Info */}
            <div className="mt-6 space-y-2 text-white/70">
              <div className="flex items-center justify-center lg:justify-start gap-3">
                <div className="w-10 h-10 rounded-full bg-white/5 backdrop-blur-sm flex items-center justify-center border border-white/5">
                  <FaPhone className="text-micropoint-gold text-sm" />
                </div>
                <a href="tel:+254703802020" className="hover:text-micropoint-gold transition-colors">
                  +254 703 802 020
                </a>
              </div>
              <div className="flex items-center justify-center lg:justify-start gap-3">
                <div className="w-10 h-10 rounded-full bg-white/5 backdrop-blur-sm flex items-center justify-center border border-white/5">
                  <FaEnvelope className="text-micropoint-gold text-sm" />
                </div>
                <a href="mailto:info@micropointsystems.com" className="hover:text-micropoint-gold transition-colors">
                info@micropointsystems.com
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right Content - CTA Card with Glass Effect */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/5 backdrop-blur-md rounded-3xl p-8 md:p-10 border border-white/5 shadow-2xl hover:border-white/10 transition-all"
          >
            <div className="text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 font-display">
                Get Started <span className="text-micropoint-gold">Today</span>
              </h3>
              <p className="text-white/50 text-sm md:text-base mb-6 max-w-sm mx-auto">
                Schedule a personalized demo and see how we can help transform your business.
              </p>
              
              <div className="space-y-4">
                <Link
                  to="/demo"
                  className="block w-full bg-gradient-to-r from-micropoint-gold to-yellow-500 hover:from-yellow-500 hover:to-micropoint-gold text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-micropoint-gold/20 flex items-center justify-center gap-2 text-base"
                >
                  Request Demo <FaArrowRight />
                </Link>
                
                <div className="grid grid-cols-2 gap-3">
                  <Link
                    to="/products"
                    className="bg-white/5 hover:bg-white/10 text-white/80 hover:text-white px-4 py-2.5 rounded-lg font-medium transition-all text-sm border border-white/5 hover:border-white/20"
                  >
                    Explore Products
                  </Link>
                  <Link
                    to="/contact"
                    className="bg-white/5 hover:bg-white/10 text-white/80 hover:text-white px-4 py-2.5 rounded-lg font-medium transition-all text-sm border border-white/5 hover:border-white/20"
                  >
                    Contact Sales
                  </Link>
                </div>
              </div>

              {/* Trust indicators */}
              <div className="mt-6 pt-6 border-t border-white/5">
                <p className="text-white/30 text-xs mb-3">Trusted by organizations across East Africa</p>
                <div className="flex justify-center gap-6">
                  {['SACCOs', 'Banks', 'Microfinance', 'NGOs'].map((org, i) => (
                    <span key={i} className="text-white/20 text-xs font-medium">{org}</span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}